import React, { useState, useRef, useContext, useCallback, useEffect } from "react"
import { LoaderMedium } from "../../../components/Loaders"
import ChatMessage from "./ChatMessage"
import trcss from "../../../styles/Telerecovery.module.css"
import { HubConnectionBuilder } from "@microsoft/signalr"
import { InputTextarea } from 'primereact/inputtextarea'
import { Button } from 'primereact/button'
import { UserContext } from "../../../context/userContext"
import { telerecoveryApiUrl } from "../../../config/microservicesUrls"
import { trApiGetChatHistory, trApiSendChatMessageToParticipant } from "../telerecoveryApi"
import { Tooltip } from "primereact/tooltip"
import { ReactComponent as HistoryIcon } from "../resources/history_icon.svg"
import ExternalLinkPopup from "../ExternalLinkPopup"
import { groupMessagesByDate, getActualUTCTime, getTimeAgo } from "../Utils"
import { FileUpload } from 'primereact/fileupload'
import { Image } from 'primereact/image'
import isParticipantActive from "../../../helpers/isParticipantActive"

export default function ChatComponent({ chatConvSid, participant, onNewMessage }) {
  const [messages, setMessages] = useState([])
  const [newChatText, setNewChatText] = useState("")
  const [loadingMessages, setLoadingMessages] = useState(true)
  const [sendingMessage, setSendingMessage] = useState(false)
  const [connection, setConnection] = useState(null)
  const [joinLink, setJoinLink] = useState(null)
  const [showExternalLinkPopup, setShowExternalLinkPopup] = useState(false)
  const messagesEndRef = useRef(null)
  const [mmsFile, setMmsFile] = useState(null)
  const userCtx = useContext(UserContext)
  const { user } = userCtx.session

  const isParticipantInactive = !isParticipantActive(participant.membership_status || userCtx.participant_membership_status)

  const getMessageHistory = useCallback(() => {
    setLoadingMessages(true)
    trApiGetChatHistory(userCtx.tokenTelerecovery, chatConvSid)
      .then(
        (response) => {
          setMessages(Array.from(response))
          setLoadingMessages(false)
          scrollToBottom()
        },
        (err) => console.log(err)
      )
      .catch(() => {
        setLoadingMessages(false)
      })
  }, [chatConvSid, userCtx.tokenTelerecovery])

  useEffect(() => {
    const _connection = new HubConnectionBuilder()
      .withUrl(telerecoveryApiUrl() + "/notificationHub")
      .withAutomaticReconnect()
      .build()
    setConnection(_connection)
  }, [])

  const checkIncomingMessage = useCallback((fromId, convSid, body) => {
    if (
      fromId !== user.staff_id
    ) {
      let newMsg = {
        dateUpdated: getActualUTCTime(),
        body: body,
        fromId: fromId
      }
      if (onNewMessage) {
        onNewMessage(newMsg)
      }
      if (chatConvSid === convSid) {
        setMessages((oldArray) => [...oldArray, newMsg])
        scrollToBottom()
      }
    }
  }, [chatConvSid])

  useEffect(() => {
    getMessageHistory()
    if (connection) {
      // Clean up previous listeners to avoid multiple subscriptions
      connection.off("IncomingChatMessage")

      // Only attempt to start the connection if it's disconnected
      if (connection.state === "Disconnected") {
        connection.start()
          .then(() => {
            connection.on("IncomingChatMessage", (fromId, convSid, body) => {
              checkIncomingMessage(fromId, convSid, body)
            })
          })
          .catch((e) => console.log("Connection failed: ", e))
      } else {
        // Re-subscribe to the event
        connection.on("IncomingChatMessage", (fromId, convSid, body) => {
          checkIncomingMessage(fromId, convSid, body)
        })
      }
      // Cleanup function to unregister event handlers
      return () => {
        if (connection) {
          connection.off("IncomingChatMessage")
        }
      }
    }
  }, [connection, checkIncomingMessage, getMessageHistory])

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: "smooth" })
  }

  const sendMessage = () => {
    if ((newChatText === "" && mmsFile === null) || sendingMessage) {
      return
    }

    setSendingMessage(true)

    const data = new FormData()
    data.append('body', newChatText)
    data.append('fromId', user.staff_id)
    data.append('convSid', chatConvSid)
    if (mmsFile) {
      data.append('file', mmsFile)
    }

    trApiSendChatMessageToParticipant(userCtx.tokenTelerecovery, data)
      .then(
        (response) => {
          let body = data.get('body')
          if (response.fileUrl && response.fileUrl !== "") {
            body += `\r\n${response.fileUrl}`
          }
          let newMsg = {
            dateUpdated: getActualUTCTime(),
            body: body,
            fromId: user.staff_id
          }
          if (onNewMessage) {
            onNewMessage(newMsg)
          }
          setMessages((oldArray) => [...oldArray, newMsg])
          setSendingMessage(false)
          setNewChatText("")
          setMmsFile(null)
          scrollToBottom()
        },
        (err) => console.log(err)
      )
  }

  const uploadHandler = (event) => {
    const file = event.files[0]
    setMmsFile(file)
    event.options.clear()
  }

  return (
    <div
      className={trcss.chatWindow}
    >
      <ExternalLinkPopup
        link={joinLink}
        showPopup={showExternalLinkPopup}
        setShowPopup={setShowExternalLinkPopup}
      />
      <div className={trcss.chatWindowHeader}>
        <div>
          <span className={trcss.userInitials}>{participant.initials}</span>
          <span style={{ marginLeft: "6px", fontWeight: "600", fontSize: "12px" }}>{participant.name}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          {messages && messages.length > 0 && (
            <p style={{ fontSize: "11px", alignSelf: "center" }}>
              {`Last message: ${getTimeAgo(messages[messages.length - 1].dateUpdated)}`}
            </p>
          )}
          <i
            className="pi pi-info-circle"
            style={{ fontSize: '20px', color: "#8467ad" }}
            id="info_icon" />
          <Tooltip target="#info_icon" className="custom-tooltip" position='top' >
            <span>
              SMS/Email notifications for new chat messages can be muted from the Settings tab in Notifications
            </span>
          </Tooltip>
          <i
            id="history_icon" >
            <HistoryIcon />
          </i>
          <Tooltip target="#history_icon" className="custom-tooltip" position='bottom'>
            <span>
              Only messages within last 90 days are displayed
            </span>
          </Tooltip>
        </div>
      </div>
      <div className={trcss.chatHistoryContainer}>
        {loadingMessages === true && <LoaderMedium />}
        {(!messages || messages.length === 0) && !loadingMessages && (
          <span className={trcss.convEmptyMsg}>
            This conversation is empty.
          </span>
        )}
        <ul className={trcss.chatWindowList} style={{ paddingTop: "10px" }}>
          {!loadingMessages &&
            messages &&
            groupMessagesByDate(messages, "fromId").map((grupedMessage, i) => (
              <li key={"msg-" + i}>
                <ChatMessage
                  messages={grupedMessage.messages}
                  fromName={participant.name}
                  fromInitials={participant.initials}
                  setJoinLink={setJoinLink}
                  setShowExternalLinkPopup={setShowExternalLinkPopup}
                ></ChatMessage>
              </li>
            ))}
        </ul>
        <div ref={messagesEndRef}></div>
      </div>
      {mmsFile && mmsFile.objectURL &&
        <div className={trcss.mms_preview_container}>
          <Image
            src={mmsFile.objectURL}
            alt={mmsFile.name}
            width="150"
            preview
            className={trcss.mms}
          />
          <Button
            className={'p-button-rounded p-button-icon-only p-button-sm'}
            icon="pi pi-times"
            onClick={() => setMmsFile(null)}
          />
        </div>
      }
      <div className={trcss.chatWindowFooter}>
        <InputTextarea
          placeholder="Type your message"
          autoResize
          className={trcss.newMessageBox}
          value={newChatText}
          onChange={(e) => setNewChatText(e.target.value)}
          rows={1}
          disabled={isParticipantInactive}
        />
        <FileUpload
          className={trcss.mms_file_upload}
          accept="image/jpeg,image/jpg,image/gif,image/png"
          maxFileSize={4000000}
          auto
          customUpload
          uploadHandler={uploadHandler}
          chooseOptions={{
            className: 'p-button-icon-only',
            icon: 'pi pi-paperclip',
            iconPos: 'left'
          }}
          id={'fileUpload'}
          disabled={isParticipantInactive}
        />
        <Tooltip target="#fileUpload" className="custom-tooltip" position='top' autoHide={false}>
          <span>Attach an image to your message</span>
        </Tooltip>
        <Button
          className={!sendingMessage ? trcss.sendBtn : trcss.sendBtnLoading}
          onClick={() => sendMessage()}
          loading={sendingMessage}
          disabled={isParticipantInactive || sendingMessage}
        />
      </div>
    </div>
  )
}